import { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { rowSpanFor } from '../utils/rowSpan';

/**
 * Meet de hoogte van het element achter `ref` en geeft het aantal rasterrijen terug.
 * useLayoutEffect: de eerste meting gebeurt vóór het tekenen, dus blokken springen niet.
 * ResizeObserver: opnieuw meten bij uitklappen, andere schermbreedte of later geladen fonts.
 * Gemeten wordt de layouthoogte (offsetHeight/borderBoxSize), niet getBoundingClientRect:
 * die telt CSS-transforms mee.
 * flushSync in de observer: een correctie (bv. een "Meer lezen"-knop die er bij het laden
 * bij komt) wordt vóór het tekenen toegepast, zodat blokken niet één frame overlappen.
 */
export function useRowSpan<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [span, setSpan] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = (height: number) => setSpan(rowSpanFor(height));
    apply(el.offsetHeight);
    const observer = new ResizeObserver(([entry]) =>
      flushSync(() => apply(entry?.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight)),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, span };
}
